/*
 * What a failed Google/Discord sign-in tells the user, keyed by the backend's
 * stable `reason` token (backend-nextja src/server/oauth.ts, FailureReason).
 *
 * The failure redirect also carries a ready-made `message` sentence, but it
 * arrives in a URL anyone can craft — `/#/auth/callback?error=x&message=…` —
 * and would be rendered in the panel's own styling on the sign-in page. So it
 * is never shown: `reason` only ever selects one of the notices below, and
 * anything unknown gets SIGN_IN_FAILED.
 *
 * Used by App.vue (a failure that lands in `location.search`) and by
 * AuthCallback.vue (one inside the hash), so the two can never disagree.
 */

export const SIGN_IN_FAILED = {
  severity: "error",
  title: "We couldn't sign you in",
  detail: "Nothing was changed. Try again, or sign in with your email and password.",
};

export const OAUTH_FAILURE_NOTICES = {
  cancelled: {
    severity: "info",
    title: "Sign-in cancelled",
    detail: "It stopped before it finished. Press the button again if that wasn't deliberate.",
  },
  state: {
    severity: "warning",
    title: "That sign-in link has expired",
    detail: "Start again from the sign-in page. An old link, or one opened in a different browser, can't be used.",
  },
  provider: {
    severity: "error",
    title: "We couldn't reach your sign-in provider",
    detail: "Try again in a moment, or sign in with your email and password.",
  },
  email_unverified: {
    severity: "warning",
    title: "Your provider hasn't confirmed this email address",
    detail: "Confirm it with them and try again, or sign in with your email and password.",
  },
  server: {
    severity: "error",
    title: "Something went wrong while signing you in",
    detail: "Nothing was changed. Try again, or sign in with your email and password.",
  },
};

/** The notice for a `reason` token. `hasOwnProperty`, so "constructor" can't pick a prototype member. */
export function oauthFailureNotice(reason) {
  return Object.prototype.hasOwnProperty.call(OAUTH_FAILURE_NOTICES, reason)
    ? OAUTH_FAILURE_NOTICES[reason]
    : SIGN_IN_FAILED;
}

/** The same notice as one line, for the sign-in form's error area. */
export function oauthFailureSentence(reason) {
  const notice = oauthFailureNotice(reason);
  return `${notice.title}. ${notice.detail}`;
}
